import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaRedo, FaCrown, FaTrophy, FaPlay } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';

// ─── Game Constants ───
const CANVAS_W = 400;
const CANVAS_H = 500;
const GROUND_H = 40;
const PLAYER_SIZE = 36;
const TROPHY_SIZE = 32;
const BASE_SPEED = 2.2;
const SPEED_INCREMENT = 0.12;       // speed increase per score
const MAX_SPEED = 6;
const GRAVITY = 0.28;               // smooth parabolic arc
const JUMP_FORCE = -7.5;            // pairs with gravity for a nice high arc
const TARGET_FPS = 60;              // physics tuned for 60fps
const LANE_TOP = 60;                // playable area top (more headroom)
const LANE_BOT = CANVAS_H - GROUND_H - PLAYER_SIZE;
const OBSTACLE_TYPES = [
  { emoji: '📚', h: 30 }, { emoji: '🔬', h: 32 }, { emoji: '🧪', h: 28 },
  { emoji: '📝', h: 26 }, { emoji: '⚗️', h: 30 }, { emoji: '🎒', h: 28 },
  { emoji: '🧲', h: 28 }, { emoji: '🌵', h: 34 }, { emoji: '🦖', h: 32 },
];
const GATE_SCORE_INTERVAL = 10;     // quiz gate every 10 pts

const CatchMe = () => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const gsRef = useRef(null);

  const [phase, setPhase] = useState('start');
  // phases: start | playing | quiz | dead | result
  const [playerName, setPlayerName] = useState('');
  const [finalScore, setFinalScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [highScore, setHighScore] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizSelected, setQuizSelected] = useState(null);
  const [gatesCleared, setGatesCleared] = useState(0);

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(async () => {
    const { data } = await supabase
      .from('catch_me_scores')
      .select('*')
      .order('score', { ascending: false })
      .limit(20);
    setLeaderboard(data || []);
  }, []);

  // Fetch quiz questions for gates
  const fetchQuizQuestions = useCallback(async () => {
    const { data } = await supabase.from('quiz_questions').select('*');
    if (data) setQuizQuestions(data);
  }, []);

  useEffect(() => {
    fetchLeaderboard();
    fetchQuizQuestions();
  }, [fetchLeaderboard, fetchQuizQuestions]);

  // ─── Pick a random quiz for gate ───
  const pickQuiz = useCallback(() => {
    if (quizQuestions.length === 0) return null;
    const q = quizQuestions[Math.floor(Math.random() * quizQuestions.length)];
    return {
      question: q.question,
      options: [
        { key: 'a', text: q.option_a },
        { key: 'b', text: q.option_b },
        { key: 'c', text: q.option_c },
        { key: 'd', text: q.option_d },
      ],
      correct: q.correct_option,
    };
  }, [quizQuestions]);

  // ─── Init Game State ───
  const initGameState = useCallback(() => ({
    player: { x: 60, y: LANE_BOT, vy: 0, onGround: true },
    trophy: { x: 180, y: LANE_BOT - 10, bobPhase: 0 },
    obstacles: [],
    particles: [],
    floatingTexts: [],
    score: 0,
    distance: 0,
    frameCount: 0,
    speed: BASE_SPEED,
    nextObstacleIn: 200,
    nextGateScore: GATE_SCORE_INTERVAL,
    gateActive: false,   // gate on screen?
    gate: null,
    gameOver: false,
    groundOffset: 0,
    bgStars: Array.from({ length: 30 }, () => ({
      x: Math.random() * CANVAS_W,
      y: Math.random() * (LANE_TOP + 60),
      size: Math.random() * 2 + 1,
      twinkle: Math.random() * Math.PI * 2,
    })),
    bgClouds: Array.from({ length: 4 }, () => ({
      x: Math.random() * CANVAS_W,
      y: LANE_TOP + Math.random() * 80,
      w: 40 + Math.random() * 40,
      speed: 0.2 + Math.random() * 0.3,
    })),
  }), []);

  // ─── Spawn helpers ───
  const spawnParticles = (gs, x, y, color, count = 5) => {
    for (let i = 0; i < count; i++) {
      gs.particles.push({
        x, y,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 1) * 3,
        life: 1, color,
        size: Math.random() * 4 + 2,
      });
    }
  };

  const spawnFloatingText = (gs, x, y, text, color) => {
    gs.floatingTexts.push({ x, y, text, color, life: 1 });
  };

  const spawnObstacle = useCallback((gs) => {
    const type = OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)];
    // flying obstacles only appear at higher scores and less often
    const isFlying = Math.random() < 0.15 && gs.score > 12;
    const y = isFlying
      ? LANE_BOT - 20 - Math.random() * 40
      : LANE_BOT + PLAYER_SIZE - type.h;
    gs.obstacles.push({
      x: CANVAS_W + 10,
      y,
      w: 30,
      h: type.h,
      emoji: type.emoji,
      scored: false,
    });
    // More generous spacing — shrinks slower
    gs.nextObstacleIn = Math.max(140, 260 - gs.score * 2);
  }, []);

  const spawnGate = useCallback((gs) => {
    gs.gate = {
      x: CANVAS_W + 10,
      w: 50,
      open: false,
    };
    gs.gateActive = true;
  }, []);

  // ─── Jump ───
  const jump = useCallback(() => {
    const gs = gsRef.current;
    if (!gs || gs.gameOver) return;
    if (gs.player.onGround) {
      gs.player.vy = JUMP_FORCE;
      gs.player.onGround = false;
      spawnParticles(gs, gs.player.x + PLAYER_SIZE / 2, gs.player.y + PLAYER_SIZE, 'rgba(99,102,241,0.5)', 3);
    }
  }, []);

  // ─── Update ───
  const updateGame = useCallback((gs) => {
    if (gs.gameOver) return;
    const dt = gs.dt || 1; // delta-time multiplier (1.0 at 60fps)
    gs.frameCount++;
    gs.speed = Math.min(MAX_SPEED, BASE_SPEED + gs.score * SPEED_INCREMENT);
    const spd = gs.speed * dt;
    gs.groundOffset = (gs.groundOffset + spd) % 40;

    // When gate is scrolling in, only move the gate and visuals — skip gameplay
    if (gs.gateActive && gs.gate) {
      gs.gate.x -= spd;
      gs.particles = gs.particles.filter(pt => {
        pt.x += pt.vx * dt; pt.y += pt.vy * dt; pt.life -= 0.03 * dt;
        return pt.life > 0;
      });
      gs.floatingTexts = gs.floatingTexts.filter(ft => {
        ft.y -= 1.5 * dt; ft.life -= 0.025 * dt;
        return ft.life > 0;
      });
      gs.bgClouds.forEach(c => {
        c.x -= c.speed * dt;
        if (c.x + c.w < 0) { c.x = CANVAS_W + 20; c.y = LANE_TOP + Math.random() * 80; }
      });
      gs.trophy.bobPhase += 0.06 * dt;
      gs.trophy.y = LANE_BOT - 10 + Math.sin(gs.trophy.bobPhase) * 12;
      return;
    }

    gs.distance += spd;

    // Player physics (dt-scaled)
    const p = gs.player;
    p.vy += GRAVITY * dt;
    p.y += p.vy * dt;
    if (p.y >= LANE_BOT) {
      p.y = LANE_BOT;
      p.vy = 0;
      p.onGround = true;
    }
    if (p.y < LANE_TOP - 20) {
      p.y = LANE_TOP - 20;
      p.vy = 0;
    }

    // Trophy bobs ahead
    const tr = gs.trophy;
    tr.bobPhase += 0.06 * dt;
    tr.y = LANE_BOT - 10 + Math.sin(tr.bobPhase) * 12;
    const desiredX = p.x + 120 + Math.sin(tr.bobPhase * 0.5) * 20;
    tr.x += (desiredX - tr.x) * 0.03 * dt;
    tr.x = Math.min(tr.x, CANVAS_W - 50);

    // Should we spawn a gate?
    if (gs.score >= gs.nextGateScore && !gs.gateActive) {
      spawnGate(gs);
      gs.nextGateScore += GATE_SCORE_INTERVAL;
      return;
    }

    // Spawn obstacles
    gs.nextObstacleIn -= spd;
    if (gs.nextObstacleIn <= 0) {
      spawnObstacle(gs);
    }

    // Update obstacles
    gs.obstacles.forEach(obs => {
      obs.x -= spd;
      if (!obs.scored && obs.x + obs.w < p.x) {
        obs.scored = true;
        gs.score++;
        spawnFloatingText(gs, p.x + 40, p.y, '+1', '#10b981');
      }
      if (
        p.x + PLAYER_SIZE - 12 > obs.x + 8 &&
        p.x + 12 < obs.x + obs.w - 8 &&
        p.y + PLAYER_SIZE - 10 > obs.y + 8 &&
        p.y + 10 < obs.y + obs.h - 8
      ) {
        gs.gameOver = true;
        spawnParticles(gs, p.x + PLAYER_SIZE / 2, p.y + PLAYER_SIZE / 2, 'rgba(239,68,68,0.8)', 12);
      }
    });

    // Particles
    gs.particles = gs.particles.filter(pt => {
      pt.x += pt.vx * dt; pt.y += pt.vy * dt; pt.life -= 0.03 * dt;
      return pt.life > 0;
    });
    gs.floatingTexts = gs.floatingTexts.filter(ft => {
      ft.y -= 1.5 * dt; ft.life -= 0.025 * dt;
      return ft.life > 0;
    });

    // Clouds
    gs.bgClouds.forEach(c => {
      c.x -= c.speed * dt;
      if (c.x + c.w < 0) { c.x = CANVAS_W + 20; c.y = LANE_TOP + Math.random() * 80; }
    });

    // Cleanup
    gs.obstacles = gs.obstacles.filter(o => o.x > -60);
  }, [spawnObstacle, spawnGate]);

  // ─── Draw ───
  const drawGame = useCallback((ctx, gs) => {
    const w = CANVAS_W, h = CANVAS_H;

    // Sky
    const skyGrad = ctx.createLinearGradient(0, 0, 0, h - GROUND_H);
    skyGrad.addColorStop(0, '#0f172a');
    skyGrad.addColorStop(0.4, '#1e3a5f');
    skyGrad.addColorStop(1, '#334155');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, h - GROUND_H);

    // Stars
    gs.bgStars.forEach(s => {
      s.twinkle += 0.02;
      ctx.fillStyle = `rgba(255,255,255,${0.3 + Math.sin(s.twinkle) * 0.3})`;
      ctx.beginPath(); ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2); ctx.fill();
    });

    // Clouds
    gs.bgClouds.forEach(c => {
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.beginPath();
      ctx.ellipse(c.x + c.w / 2, c.y, c.w / 2, 12, 0, 0, Math.PI * 2);
      ctx.fill();
    });

    // Ground
    const gGrad = ctx.createLinearGradient(0, h - GROUND_H, 0, h);
    gGrad.addColorStop(0, '#059669'); gGrad.addColorStop(1, '#047857');
    ctx.fillStyle = gGrad;
    ctx.fillRect(0, h - GROUND_H, w, GROUND_H);
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    for (let gx = -gs.groundOffset; gx < w; gx += 40) {
      ctx.beginPath(); ctx.moveTo(gx, h - GROUND_H); ctx.lineTo(gx + 20, h); ctx.stroke();
    }

    // Obstacles
    gs.obstacles.forEach(obs => {
      ctx.font = `${obs.h - 4}px serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(obs.emoji, obs.x + obs.w / 2, obs.y + obs.h / 2);
    });

    // Gate
    if (gs.gate && gs.gateActive) {
      const g = gs.gate;
      const gateH = h - GROUND_H;
      // Left pillar
      ctx.fillStyle = '#6366f1';
      ctx.fillRect(g.x, 0, 12, gateH);
      ctx.fillRect(g.x + g.w - 12, 0, 12, gateH);
      // Top bar
      ctx.fillStyle = '#4f46e5';
      ctx.fillRect(g.x, 0, g.w, 16);
      // Gate bars (if not open)
      if (!g.open) {
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 3;
        for (let gy = 20; gy < gateH; gy += 20) {
          ctx.beginPath(); ctx.moveTo(g.x + 12, gy); ctx.lineTo(g.x + g.w - 12, gy); ctx.stroke();
        }
        // Question mark
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#fbbf24';
        ctx.fillText('❓', g.x + g.w / 2, gateH / 2);
      } else {
        // Open gate — green glow
        ctx.fillStyle = 'rgba(16,185,129,0.15)';
        ctx.fillRect(g.x + 12, 16, g.w - 24, gateH - 16);
        ctx.font = 'bold 28px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#10b981';
        ctx.fillText('✅', g.x + g.w / 2, gateH / 2);
      }
    }

    // Trophy (always ahead)
    const tr = gs.trophy;
    ctx.save();
    ctx.shadowColor = '#fbbf24';
    ctx.shadowBlur = 12 + Math.sin(gs.frameCount * 0.1) * 4;
    ctx.font = `${TROPHY_SIZE}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🏆', tr.x + TROPHY_SIZE / 2, tr.y + TROPHY_SIZE / 2);
    ctx.shadowBlur = 0;
    ctx.restore();

    // Player (hand)
    const p = gs.player;
    ctx.save();
    ctx.font = `${PLAYER_SIZE}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Slight tilt when jumping
    if (!p.onGround) {
      ctx.translate(p.x + PLAYER_SIZE / 2, p.y + PLAYER_SIZE / 2);
      ctx.rotate(Math.min(p.vy * 0.03, 0.4));
      ctx.fillText('✋', 0, 0);
    } else {
      ctx.fillText('✋', p.x + PLAYER_SIZE / 2, p.y + PLAYER_SIZE / 2);
    }
    ctx.restore();

    // Particles
    gs.particles.forEach(pt => {
      ctx.globalAlpha = pt.life;
      ctx.fillStyle = pt.color;
      ctx.beginPath(); ctx.arc(pt.x, pt.y, pt.size * pt.life, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    });

    // Floating texts
    gs.floatingTexts.forEach(ft => {
      ctx.globalAlpha = ft.life;
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = ft.color;
      ctx.fillText(ft.text, ft.x, ft.y);
      ctx.globalAlpha = 1;
    });

    // HUD
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath(); ctx.roundRect(w / 2 - 50, 8, 100, 34, 17); ctx.fill();
    ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff';
    ctx.fillText(`🏆 ${gs.score}`, w / 2, 30);

    // Speed indicator
    const lvl = Math.floor((gs.speed - BASE_SPEED) / SPEED_INCREMENT) + 1;
    if (lvl > 1) {
      ctx.font = '10px sans-serif';
      ctx.fillStyle = 'rgba(250,204,21,0.7)';
      ctx.fillText(`⚡ Lv.${Math.min(lvl, 99)}`, w / 2, 50);
    }

    // Gate counter
    const cleared = gs.nextGateScore / GATE_SCORE_INTERVAL - 1;
    if (cleared > 0) {
      ctx.font = '10px sans-serif';
      ctx.fillStyle = 'rgba(99,102,241,0.8)';
      ctx.textAlign = 'left';
      ctx.fillText(`🚪 ${cleared}`, 10, 22);
    }

    // Game over overlay
    if (gs.gameOver) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, w, h);
      ctx.font = 'bold 28px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ef4444';
      ctx.fillText('💥 Game Over!', w / 2, h / 2 - 30);
      ctx.font = 'bold 20px sans-serif';
      ctx.fillStyle = '#fbbf24';
      ctx.fillText(`Skor: ${gs.score}`, w / 2, h / 2 + 10);
      ctx.font = '14px sans-serif';
      ctx.fillStyle = '#94a3b8';
      ctx.fillText('Klik untuk lanjut', w / 2, h / 2 + 45);
    }
  }, []);

  // ─── Game Loop (delta-time for consistent speed on all devices) ───
  const startGameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let lastTime = performance.now();

    const loop = (now) => {
      const gs = gsRef.current;
      if (!gs) return;

      // Calculate delta-time multiplier (1.0 at 60fps, ~2.0 at 30fps)
      const elapsed = now - lastTime;
      lastTime = now;
      const dt = Math.min(elapsed / (1000 / TARGET_FPS), 3); // cap at 3x to prevent huge jumps
      gs.dt = dt;

      updateGame(gs);
      drawGame(ctx, gs);

      // Check if gate reached player → trigger quiz
      if (gs.gateActive && gs.gate && !gs.gate.open && gs.gate.x <= gs.player.x + PLAYER_SIZE + 30) {
        // Pause game, show quiz
        const q = pickQuiz();
        if (q) {
          setCurrentQuiz(q);
          setQuizSelected(null);
          setPhase('quiz');
          return; // stop loop, quiz overlay takes over
        } else {
          // No quiz questions available, just open gate
          gs.gate.open = true;
          gs.gateActive = false;
          gs.gate = null;
        }
      }

      if (gs.gameOver) {
        setFinalScore(gs.score);
        setPhase('dead');
        return;
      }
      gameLoopRef.current = requestAnimationFrame(loop);
    };
    gameLoopRef.current = requestAnimationFrame((now) => { lastTime = now; loop(now); });
  }, [updateGame, drawGame, pickQuiz]);

  // ─── Start Game ───
  const startGame = useCallback(() => {
    if (!playerName.trim()) return;
    const gs = initGameState();
    gsRef.current = gs;
    setGatesCleared(0);
    setPhase('playing');
    setTimeout(() => startGameLoop(), 50);
  }, [playerName, initGameState, startGameLoop]);

  // ─── Quiz Answer ───
  const handleQuizAnswer = useCallback((key) => {
    if (!currentQuiz || quizSelected !== null) return;
    setQuizSelected(key);
    const gs = gsRef.current;

    setTimeout(() => {
      if (key === currentQuiz.correct) {
        // Correct! Open gate and resume
        if (gs && gs.gate) {
          gs.gate.open = true;
          // Give bonus points
          gs.score += 5;
          spawnFloatingText(gs, gs.player.x + 60, gs.player.y, '+5 🎉', '#10b981');
          spawnParticles(gs, gs.player.x + PLAYER_SIZE, gs.player.y, 'rgba(16,185,129,0.7)', 8);
        }
        setGatesCleared(prev => prev + 1);
        // After brief delay, remove gate and resume
        setTimeout(() => {
          if (gs) {
            gs.gateActive = false;
            gs.gate = null;
          }
          setCurrentQuiz(null);
          setQuizSelected(null);
          setPhase('playing');
          setTimeout(() => startGameLoop(), 50);
        }, 600);
      } else {
        // Wrong! Game Over
        if (gs) {
          gs.gameOver = true;
          spawnParticles(gs, gs.player.x + PLAYER_SIZE / 2, gs.player.y + PLAYER_SIZE / 2, 'rgba(239,68,68,0.8)', 15);
        }
        setTimeout(() => {
          setFinalScore(gs ? gs.score : 0);
          setCurrentQuiz(null);
          setQuizSelected(null);
          setPhase('dead');
          // Redraw to show game over state
          const canvas = canvasRef.current;
          if (canvas && gs) {
            drawGame(canvas.getContext('2d'), gs);
          }
        }, 800);
      }
    }, 500);
  }, [currentQuiz, quizSelected, startGameLoop, drawGame]);

  // ─── Handle Death → Result ───
  const handleDeathClick = useCallback(async () => {
    setPhase('result');
    const s = finalScore;
    if (s > highScore) setHighScore(s);
    await supabase.from('catch_me_scores').insert({
      player_name: playerName.trim(),
      score: s,
    });
    fetchLeaderboard();
  }, [finalScore, playerName, highScore, fetchLeaderboard]);

  // ─── Input Handling ───
  useEffect(() => {
    if (phase === 'playing') {
      const handleKey = (e) => {
        if (e.code === 'Space' || e.code === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
          e.preventDefault();
          jump();
        }
      };
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }
    if (phase === 'dead') {
      const handleKey = (e) => {
        if (e.code === 'Space' || e.code === 'Enter') handleDeathClick();
      };
      const timer = setTimeout(() => {
        window.addEventListener('keydown', handleKey);
      }, 500);
      return () => { clearTimeout(timer); window.removeEventListener('keydown', handleKey); };
    }
  }, [phase, jump, handleDeathClick]);

  // Cleanup
  useEffect(() => {
    return () => { if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current); };
  }, []);

  const playAgain = () => {
    if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    setPhase('start');
  };

  // ──── START SCREEN ────
  if (phase === 'start') {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">✋🏆</div>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1">{t('catchMe.title')}</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('catchMe.subtitle')}</p>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-emerald-50 dark:from-indigo-900/20 dark:to-emerald-900/20 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FaPlay className="text-emerald-500" /> {t('catchMe.howToPlay')}
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li className="flex items-start gap-2"><span>✋</span> {t('catchMe.rule1')}</li>
              <li className="flex items-start gap-2"><span>🏆</span> {t('catchMe.rule2')}</li>
              <li className="flex items-start gap-2"><span>🚪</span> {t('catchMe.rule3')}</li>
              <li className="flex items-start gap-2"><span>⚡</span> {t('catchMe.rule4')}</li>
            </ul>
          </div>

          {highScore > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 mb-4 text-center">
              <span className="text-sm text-amber-700 dark:text-amber-400 font-bold">🏆 Rekor kamu: {highScore}</span>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('quiz.yourName')}</label>
            <input
              type="text"
              value={playerName}
              onChange={e => setPlayerName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && startGame()}
              placeholder={t('quiz.namePlaceholder')}
              maxLength={30}
              className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm"
            />
          </div>

          <button
            onClick={startGame}
            disabled={!playerName.trim()}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-40 text-lg flex items-center justify-center gap-2"
          >
            🚀 {t('catchMe.startGame')}
          </button>
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaTrophy className="text-amber-500" /> {t('quiz.leaderboard')}
            </h3>
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((entry, i) => (
                <div key={entry.id} className={`flex items-center gap-3 p-2.5 rounded-xl ${i < 3 ? 'bg-gradient-to-r from-amber-50 to-emerald-50 dark:from-amber-900/20 dark:to-emerald-900/20' : 'bg-gray-50 dark:bg-dark-700'}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                    backgroundColor: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#e5e7eb',
                    color: i < 3 ? 'white' : '#6b7280',
                  }}>
                    {i < 3 ? <>{i === 0 ? '👑' : i === 1 ? '🥈' : '🥉'}</> : i + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex-1 truncate">{entry.player_name}</span>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{entry.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ──── PLAYING / DEAD / QUIZ SCREEN ────
  if (phase === 'playing' || phase === 'dead' || (phase === 'quiz' && currentQuiz)) {
    return (
      <div className="space-y-4">
        <div className="flex justify-center">
          <div className="relative" style={{ maxWidth: CANVAS_W, width: '100%' }}>
            <canvas
              ref={canvasRef}
              width={CANVAS_W}
              height={CANVAS_H}
              onClick={() => {
                if (phase === 'playing') jump();
                if (phase === 'dead') handleDeathClick();
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                if (phase === 'playing') jump();
                if (phase === 'dead') handleDeathClick();
              }}
              className={`rounded-2xl shadow-xl border-2 cursor-pointer w-full ${
                phase === 'quiz'
                  ? 'border-indigo-400 dark:border-indigo-600'
                  : 'border-gray-200 dark:border-dark-600'
              }`}
              style={{ touchAction: 'none', width: '100%', height: 'auto' }}
            />

            {/* Quiz overlay — positioned on top of canvas */}
            {phase === 'quiz' && currentQuiz && (
              <div className="absolute inset-0 flex items-center justify-center rounded-2xl overflow-hidden z-10">
                {/* Dark backdrop */}
                <div className="absolute inset-0 bg-black/60 rounded-2xl" />
                {/* Quiz card */}
                <div className="relative z-20 w-[90%] max-h-[92%] overflow-y-auto bg-white/95 dark:bg-dark-800/95 backdrop-blur rounded-xl shadow-2xl border border-indigo-400 dark:border-indigo-500 p-3 sm:p-4">
                  <div className="text-center mb-2">
                    <span className="text-2xl">🚪❓</span>
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm mt-0.5">{t('catchMe.gateTitle')}</h3>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center leading-relaxed">
                    {currentQuiz.question}
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {currentQuiz.options.map(opt => {
                      let btnClass = 'w-full text-left px-3 py-2 sm:py-2.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-all ';
                      if (quizSelected === null) {
                        btnClass += 'border-gray-200 dark:border-dark-600 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-gray-700 dark:text-gray-200 cursor-pointer';
                      } else if (opt.key === currentQuiz.correct) {
                        btnClass += 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
                      } else if (opt.key === quizSelected) {
                        btnClass += 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400';
                      } else {
                        btnClass += 'border-gray-200 dark:border-dark-600 text-gray-400 dark:text-gray-500';
                      }
                      return (
                        <button
                          key={opt.key}
                          onClick={() => handleQuizAnswer(opt.key)}
                          disabled={quizSelected !== null}
                          className={btnClass}
                        >
                          <span className="font-bold mr-1.5">{opt.key.toUpperCase()}.</span>
                          {opt.text}
                        </button>
                      );
                    })}
                  </div>
                  {quizSelected !== null && (
                    <div className={`mt-2 text-center text-xs sm:text-sm font-bold ${quizSelected === currentQuiz.correct ? 'text-emerald-600' : 'text-red-600'}`}>
                      {quizSelected === currentQuiz.correct ? '✅ Benar! Gerbang terbuka!' : '❌ Salah! Game Over!'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-gray-400 dark:text-gray-500">
          {t('catchMe.controlHint')}
        </p>
      </div>
    );
  }

  // ──── RESULT SCREEN ────
  const playerRank = leaderboard.findIndex(e => e.score <= finalScore);
  const rank = playerRank === -1 ? leaderboard.length + 1 : playerRank + 1;
  const isNewRecord = finalScore >= highScore && finalScore > 0;

  const grade = finalScore >= 50 ? { emoji: '🏆', label: 'Master PIMNAS!', color: 'from-yellow-400 to-amber-500' }
    : finalScore >= 30 ? { emoji: '🌟', label: 'Alumni Hebat!', color: 'from-emerald-500 to-teal-500' }
    : finalScore >= 15 ? { emoji: '👍', label: 'Lumayan!', color: 'from-blue-500 to-cyan-500' }
    : finalScore >= 5 ? { emoji: '📚', label: 'Perlu Latihan!', color: 'from-indigo-500 to-purple-500' }
    : { emoji: '💪', label: 'Coba Lagi!', color: 'from-gray-500 to-gray-600' };

  return (
    <div className="space-y-6">
      {/* Result Header */}
      <div className={`bg-gradient-to-r ${grade.color} rounded-2xl p-8 text-center`}>
        <div className="text-6xl mb-3">{grade.emoji}</div>
        <h2 className="text-2xl font-extrabold text-white mb-1">{grade.label}</h2>
        <p className="text-white/80">{playerName}</p>
        {isNewRecord && (
          <div className="mt-2 inline-block bg-white/20 rounded-full px-4 py-1 text-sm font-bold text-white animate-pulse">
            🎉 Rekor Baru!
          </div>
        )}
      </div>

      {/* Score Card */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 p-6 text-center">
        <div className="text-5xl font-extrabold text-gray-900 dark:text-white mb-1">{finalScore}</div>
        <p className="text-gray-400 text-sm mb-4">{t('quiz.points')}</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-emerald-600">#{rank}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.rank')}</p>
          </div>
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-indigo-600">🚪 {gatesCleared}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('catchMe.gatesCleared')}</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
            <p className="text-xl font-bold text-amber-600">{highScore}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Rekor</p>
          </div>
        </div>

        <button onClick={playAgain} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all flex items-center justify-center gap-2">
          <FaRedo /> {t('quiz.playAgain')}
        </button>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FaCrown className="text-amber-500" /> {t('quiz.globalLeaderboard')}
          </h3>
          <div className="space-y-2">
            {leaderboard.map((entry, i) => {
              const isYou = entry.player_name === playerName.trim() && entry.score === finalScore;
              return (
                <div key={entry.id} className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${isYou ? 'bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-emerald-400' : i < 3 ? 'bg-gradient-to-r from-amber-50 to-emerald-50 dark:from-amber-900/20 dark:to-emerald-900/20' : 'bg-gray-50 dark:bg-dark-700'}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                    backgroundColor: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#e5e7eb',
                    color: i < 3 ? 'white' : '#6b7280',
                  }}>
                    {i < 3 ? <>{i === 0 ? '👑' : i === 1 ? '🥈' : '🥉'}</> : i + 1}
                  </span>
                  <span className={`text-sm font-medium flex-1 truncate ${isYou ? 'text-emerald-700 dark:text-emerald-400 font-bold' : 'text-gray-700 dark:text-gray-200'}`}>
                    {entry.player_name} {isYou && <span className="text-xs">({t('quiz.you')})</span>}
                  </span>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{entry.score}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CatchMe;
